import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/skip';
import { WorkflowType } from './../../clients/client/header/onboard-request.model';
import {
  ActiveAuditFilter,
  AUDIT_DATE_PRESETS,
  AUDIT_EVENT_TYPES,
  AuditEventTypeOption,
  AuditFilter
} from './audit-filter.model';
import { EntityAuditService } from './entity-audit.service';
import { MdDialog, MdDialogRef, MdDialogConfig } from '@angular/material';
import { Component, OnInit, OnDestroy, ViewContainerRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';

import { ConstantsService } from 'app/services/constants-service';
import { EntityOtherComponent } from 'app/entities/other/entity-other.component';
import { EntityIdDetails } from 'app/shared/models/entity-id-details.model';
import { EntityOtherService } from 'app/entities/other/entity-other.service';
import { EntityDetailsService } from 'app/entities/details/entity-details.service';
import { User } from 'app/shared/models/user.model';
import { WarmUpService } from 'app/services/warmup/warmup.service';
import { AddAuditInformationComponent } from 'app/entities/audit/addauditinformation/add-audit-information.component';

@Component({
  templateUrl: './entity-audit.component.html',
  styleUrls: ['./entity-audit.component.css']
})

export class EntityAuditComponent extends EntityOtherComponent implements OnInit, OnDestroy {
  entityIdDetails: EntityIdDetails;
  userCtrl: FormControl;
  users: User[];
  workflowTypeValues: Array<string>;
  filters: AuditFilter;
  selectedUser: number;
  selectedUserName: string;
  selectedWorkflow: string = null;
  selectedStartDate: Date | string;
  selectedEndDate: Date | string;
  selectedEventTypes: string[] = [];
  selectedDatePreset = 'all';
  sortOrder: 'desc' | 'asc' = 'desc';
  searchCtrl: FormControl;
  eventTypeOptions: AuditEventTypeOption[] = AUDIT_EVENT_TYPES;
  datePresets = AUDIT_DATE_PRESETS;
  activeFilters: ActiveAuditFilter[] = [];
  filteredUsers: Observable<User[]>;
  dialogRef: MdDialogRef<any>;
  private skipSearchTrigger = false;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public entityDetailsService: EntityDetailsService,
    public domSanitizer: DomSanitizer,
    public entityOtherService: EntityOtherService,
    private warmUpService: WarmUpService,
    public dialog: MdDialog,
    public viewContainerRef: ViewContainerRef,
    private entityAuditService: EntityAuditService) {
    super(route, router, domSanitizer, entityDetailsService, entityOtherService);
  }

  ngOnInit() {
    document.body.classList.add('audit-refine-dropdowns-active');
    this.entityIdDetails = this.entityDetailsService.getEntityIdDetails();
    this.entityDetailsService.entityDetailsUpdated.subscribe(
      (entityDetails) => {
        this.entityIdDetails = entityDetails;
        this.getAuditDetails();
      }
    );
    this.userCtrl = new FormControl('');
    this.searchCtrl = new FormControl('');
    this.setUpUsersList();
    this.setUpSearch();
    this.workflowTypeValues = Array.from(ConstantsService.WORKFLOWTYPE_MAP.values());
    this.getAuditDetails();
  }

  ngOnDestroy() {
    document.body.classList.remove('audit-refine-dropdowns-active');
  }

  setUpSearch() {
    this.searchCtrl.valueChanges
      .debounceTime(300)
      .distinctUntilChanged()
      .skip(1)
      .subscribe(() => {
        if (!this.skipSearchTrigger) {
          this.getAuditDetails();
        }
      });
  }

  setUpUsersList() {
    this.users = this.warmUpService.getUsersList();

    this.filteredUsers = this.userCtrl.valueChanges
      .startWith(null)
      .map(user => user && typeof user === 'object' ? user.name : user)
      .map(name => name ? this.filterUsers(name) : (this.users ? this.users.slice() : this.users));
  }

  filterUsers(val: string): User[] {
    return val ? this.users.filter(s => (s.name.toLowerCase().indexOf(val.toLowerCase()) === 0)) : this.users;
  }

  displayFn(user: User): string {
    return user ? user.name : '';
  }

  openAddAuditInformationDialog() {
    const config = new MdDialogConfig();
    config.viewContainerRef = this.viewContainerRef;
    this.dialogRef = this.dialog.open(AddAuditInformationComponent, config);
    this.dialogRef.componentInstance.entityIdDetails = this.entityIdDetails;
    this.dialogRef.afterClosed().subscribe(result => {
      this.dialogRef = null;
      this.entityAuditService.setAuditInformation('Add');
    });
  }

  selectUser(user: User) {
    this.selectedUser = user ? user.userId : null;
    this.selectedUserName = user ? user.name : null;
    this.getAuditDetails();
  }

  toggleEventType(eventTypeId: string) {
    const index = this.selectedEventTypes.indexOf(eventTypeId);
    if (index >= 0) {
      this.selectedEventTypes.splice(index, 1);
    } else {
      this.selectedEventTypes.push(eventTypeId);
    }
    if (!this.showWorkflowFilter()) {
      this.selectedWorkflow = null;
    }
    this.getAuditDetails();
  }

  isEventTypeSelected(eventTypeId: string): boolean {
    return this.selectedEventTypes.indexOf(eventTypeId) >= 0;
  }

  /** Workflow filter only applies when narrowing to tasks. */
  showWorkflowFilter(): boolean {
    return this.selectedEventTypes.length === 1 && this.selectedEventTypes[0] === 'TASK';
  }

  applyDatePreset(presetId: string) {
    this.selectedDatePreset = presetId;
    const preset = this.datePresets.find(item => item.id === presetId);
    if (!preset || !preset.days) {
      this.selectedStartDate = null;
      this.selectedEndDate = null;
    } else {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - preset.days);
      this.selectedStartDate = this.formatDateValue(start);
      this.selectedEndDate = this.formatDateValue(end);
    }
    this.getAuditDetails();
  }

  toggleSortOrder() {
    this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc';
    this.getAuditDetails();
  }

  getAuditDetails() {
    this.filters = new AuditFilter();
    this.filters.userId = this.selectedUser;
    this.filters.searchText = (this.searchCtrl.value || '').trim();
    this.filters.eventTypes = this.selectedEventTypes.slice();
    this.filters.sortOrder = this.sortOrder;
    let workflowType: string;
    ConstantsService.WORKFLOWTYPE_MAP.forEach((value: string, key: WorkflowType) => {
      if (value === this.selectedWorkflow) {
        workflowType = WorkflowType[key];
      }
    });
    this.filters.workflowType = workflowType;
    this.filters.startDate = this.selectedStartDate;
    this.filters.endDate = this.selectedEndDate;
    this.activeFilters = this.buildActiveFilters();
  }

  buildActiveFilters(): ActiveAuditFilter[] {
    const active: ActiveAuditFilter[] = [];
    if (this.filters.searchText) {
      active.push({ key: 'search', label: 'Search: ' + this.filters.searchText });
    }
    if (this.selectedUserName) {
      active.push({ key: 'user', label: 'User: ' + this.selectedUserName });
    }
    if (this.selectedWorkflow) {
      active.push({ key: 'workflow', label: 'Workflow: ' + this.selectedWorkflow });
    }
    if (this.selectedDatePreset !== 'all') {
      const preset = this.datePresets.find(item => item.id === this.selectedDatePreset);
      active.push({ key: 'datePreset', label: preset ? preset.label : 'Custom date range' });
    } else if (this.selectedStartDate || this.selectedEndDate) {
      active.push({ key: 'dateRange', label: 'Custom date range' });
    }
    this.selectedEventTypes.forEach(eventTypeId => {
      const option = this.eventTypeOptions.find(item => item.id === eventTypeId);
      active.push({
        key: 'eventType:' + eventTypeId,
        label: option ? option.label : eventTypeId
      });
    });
    if (this.sortOrder === 'asc') {
      active.push({ key: 'sort', label: 'Oldest first' });
    }
    return active;
  }

  removeActiveFilter(filter: ActiveAuditFilter) {
    switch (filter.key) {
      case 'search':
        this.skipSearchTrigger = true;
        this.searchCtrl.setValue('');
        this.skipSearchTrigger = false;
        break;
      case 'user':
        this.selectedUser = null;
        this.selectedUserName = null;
        this.userCtrl.setValue('');
        break;
      case 'workflow':
        this.selectedWorkflow = null;
        break;
      case 'datePreset':
      case 'dateRange':
        this.selectedDatePreset = 'all';
        this.selectedStartDate = null;
        this.selectedEndDate = null;
        break;
      case 'sort':
        this.sortOrder = 'desc';
        break;
      default:
        if (filter.key.indexOf('eventType:') === 0) {
          const eventTypeId = filter.key.replace('eventType:', '');
          this.selectedEventTypes = this.selectedEventTypes.filter(id => id !== eventTypeId);
        }
        break;
    }
    this.getAuditDetails();
  }

  clearAllFilters() {
    this.skipSearchTrigger = true;
    this.searchCtrl.setValue('');
    this.skipSearchTrigger = false;
    this.selectedUser = null;
    this.selectedUserName = null;
    this.userCtrl.setValue('');
    this.selectedWorkflow = null;
    this.selectedStartDate = null;
    this.selectedEndDate = null;
    this.selectedDatePreset = 'all';
    this.selectedEventTypes = [];
    this.sortOrder = 'desc';
    this.getAuditDetails();
  }

  clearStartDate() {
    this.selectedStartDate = null;
    this.selectedDatePreset = 'all';
    this.getAuditDetails();
  }

  clearEndDate() {
    this.selectedEndDate = null;
    this.selectedDatePreset = 'all';
    this.getAuditDetails();
  }

  onManualDateChange() {
    this.selectedDatePreset = 'custom';
    this.getAuditDetails();
  }

  private formatDateValue(date: Date): string {
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    return year + '-' + month + '-' + day;
  }
}



// WEBPACK FOOTER //
// ./src/app/entities/audit/entity-audit.component.ts